/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import PropTypes from 'prop-types';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import Tooltip from '@mui/material/Tooltip';

import { useState } from "react";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";


function SummariesSidenav({ summaries, onSummarySelect, onRegenerateSummary, onDeleteSummary, onMakeSummaryMoreConcise, subscription }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentSummary, setCurrentSummary] = useState(null);

  const handleMenuOpen = (event, summaryType) => {
    setAnchorEl(event.currentTarget);
    setCurrentSummary(summaryType);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRegenerate = () => {
    onRegenerateSummary(currentSummary);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDeleteSummary(currentSummary);
    handleMenuClose();
  };

  const handleMakeConcise = () => {
    onMakeSummaryMoreConcise(currentSummary);
    handleMenuClose();
  }


 const renderSidenavItems = Object.keys(summaries).map((summaryType, key) => {
   const itemKey = `summary-item-${key}`;

   return (
     <SoftBox key={itemKey} component="li" pt={key === 0 ? 0 : 1} p={1} display="flex" justifyContent="space-between" alignItems="center">
       <SoftTypography
         component="a"
         href={`#${summaryType}`}
         variant="button"
         fontWeight="regular"
         color="text"
         textTransform="capitalize"
         sx={({
           borders: { borderRadius },
           functions: { pxToRem },
           palette: { light },
           transitions,
         }) => ({
           display: "flex",
           alignItems: "center",
           borderRadius: borderRadius.md,
           padding: `${pxToRem(10)} ${pxToRem(16)}`,
           transition: transitions.create("background-color", {
             easing: transitions.easing.easeInOut,
             duration: transitions.duration.shorter,
           }),

           "&:hover": {
             backgroundColor: light.main,
           },
         })}
         onClick={(e) => {
           e.preventDefault();
           onSummarySelect(summaryType);
         }}
       >
         {summaryType}
       </SoftTypography>
       {/* Menu Trigger */}
       <Tooltip title="open menu" placement="top" >
        <IconButton 
          color="dark"
          onClick={(e) => handleMenuOpen(e, summaryType)}
          sx={{
            transition: "0.3s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.14)", // Slight hover background
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)", // Shadow effect
            },
          }}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <Tooltip title={subscription === "Silver Subscription" ? "🔔 Regeneration is available for Platinum and Gold Package subscribers only." : ""} placement="top">
              <div> {/* Wrap the MenuItem in a div for tooltip functionality on a potentially disabled item */}
                  <MenuItem onClick={subscription !== "Silver Subscription" ? handleRegenerate : null} disabled={subscription === "Silver Subscription"}>
                      <ListItemIcon>
                          <LoopOutlinedIcon fontSize="small" sx={{ color: subscription === "Silver Subscription" ? 'action.disabled' : '#344767' }} />
                      </ListItemIcon>
                      <ListItemText>
                          <SoftTypography color={subscription === "Silver Subscription" ? "text.disabled" : "dark"} variant="button" fontWeight="medium">
                              Regenerate Note
                          </SoftTypography>
                      </ListItemText>
                  </MenuItem>
              </div>
          </Tooltip>
          <MenuItem onClick={handleMakeConcise}>
            <ListItemIcon>
                <SummarizeOutlinedIcon fontSize="small" sx={{ color: '#344767' }} />
            </ListItemIcon>
            <ListItemText>
                <SoftTypography color="dark" variant="button" fontWeight="medium">
                    Make Concise
                </SoftTypography>
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
                <DeleteOutlinedIcon fontSize="small" sx={{ color: "red" }} />
            </ListItemIcon>
            <ListItemText>
                <SoftTypography color="error" variant="button" fontWeight="medium">
                Delete
                </SoftTypography>
            </ListItemText>
          </MenuItem>
        </Menu>
     </SoftBox>
   );
 });

 return (
   <Card
     sx={{
       borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
       position: "sticky",
       top: "3%",
       boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
       
     }}
   >
     <SoftBox
       component="ul"
       display="flex"
       flexDirection="column"
       p={2}
       m={0}
       sx={{ listStyle: "none" }}
     >
       {renderSidenavItems}
     </SoftBox>
   </Card>
 );
}

SummariesSidenav.propTypes = {
 summaries: PropTypes.objectOf(PropTypes.any).isRequired,
 onSummarySelect: PropTypes.func.isRequired,
 onRegenerateSummary: PropTypes.func,
 onDeleteSummary: PropTypes.func,
 onMakeSummaryMoreConcise: PropTypes.func,
 subscription: PropTypes.string,
};

export default SummariesSidenav;
